import React, { useEffect, useState, useMemo } from "react";
import { Layout, Menu, Avatar, Dropdown, Space, Button, Tooltip, Select, message, Modal, Badge, notification } from "antd";
import { useNavigate, useLocation, useOutlet } from "react-router-dom";
import KeepAliveTab from "../common/KeepAliveTab";
import sseService from "../../services/sseService";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HomeOutlined,
  AppstoreOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  GlobalOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  BellOutlined,
  GiftOutlined,
  SwapOutlined,
  DashboardOutlined,
  BarChartOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import NotificationCenter from "../common/NotificationCenter";

import { setCookie, getCookie } from "../../utils/cookie";
import locale from "@aligov/global-locale";

import * as authService from "../../services/auth/authService";
import { getUnreadNotificationCount } from "../../services/notification/notificationService";
import { navigateToLogin } from "../../utils/navigation";

const { Header, Sider, Content } = Layout;

// 语言标映射
const langMap = {
  // "zh-CN": "zh",
  "en-US": "en",
  "th-TH": "th",
};

// 创建带 Tooltip 的菜单项标签
const MenuItemWithTooltip = ({ label, collapsed, isLeaf = true }) => {
  // 检查文本是否可能被截断
  const shouldShowTooltip = label && (label.length > 15 || collapsed);

  if (shouldShowTooltip) {
    return (
      <Tooltip title={label} placement={collapsed ? "right" : "top"}>
        <span className="menu-item-with-tooltip">{label}</span>
      </Tooltip>
    );
  }

  return <span className="menu-item-with-tooltip">{label}</span>;
};

const MainLayout = () => {
  // 菜单默认展开
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [menuItemsData, setMenuItemsData] = useState([]);
  const [warehouseList, setWarehouseList] = useState([]);
  const [currentWarehouse, setCurrentWarehouse] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const titleKeyMap = useMemo(() => {
    const map = new Map();
    map.set('/master-data/store-management', t('menu.warehouse'));
    map.set('/master-data/item/list', t('menu.itemMgt'));
    map.set('/master-data/category/list', t('menu.categoryMgt'));
    map.set('/master-data/location/list', t('menu.locationMgt'));
    map.set('/promotion/substitute-rule', t('promotionRule.menu.substituteRule'));
    map.set('/order/order/list', t('order.order.list.title'));
    map.set('/order/return-order/list', t('order.returnOrder.list.title'));
    map.set('/order/shortage-order/list', t('order.shortageOrder.list.title'));
    map.set('/order/substitute-management/list', t('order.substituteManagement.list.title'));
    map.set('/picking/picking-task/list', t('picking.pickingTask.list.title'));
    map.set('/picking/picking-strategy/list', t('pickingStrategy.menu.title'));
    map.set('/wave/wave-rule', t('menu.waveStrategy'));
    map.set('/wave/wave-instance', t('wave.waveInstance.list.title'));
    map.set('/wave/wave-config-log', t('menu.waveConfigLog'));
    map.set('/inventory/stock/list', t('inventory.menu.logicalInventory'));
    map.set('/dashboard', t('menu.dashboard'));
    map.set('/nationwide-dashboard', t('menu.nationwideDashboard'));
    map.set('/account-management', t('account.title'));
    map.set('/role', t('role.title'));
    map.set('/personnel-mgt', t('personnel.title'));
    map.set('/report/sla-fulfillment', t('menu.slaFulfillmentReport'));
    map.set('/report/payment-invoice', t('menu.paymentInvoiceReport'));
    return map;
  }, [t]);

  // 初始化时设置多语言
  useEffect(() => {
    const { lang } = locale.getLocale();
    if (lang) {
      i18n.changeLanguage(langMap[lang] || lang);
    } else {
      i18n.changeLanguage("en-US"); // 默认语言
    }
  }, []); // 只在组件挂载时执行一次

  // 仅在已认证时获取仓库列表和未读通知数量，避免无登录态时发起无效请求导致循环跳转
  useEffect(() => {
    if (isAuthenticated) {
      fetchWarehouseList();
      fetchUnreadCount();
    }
  }, [isAuthenticated]);

  // 订阅 SSE notification 事件：收到实时通知时弹出提示并刷新未读数
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleNotification = (data) => {
      const payload = data || {};
      const title = payload.title || t("notification.center.title");
      const content = payload.content || "";

      notification.open({
        message: title,
        description: content,
        placement: "topRight",
        duration: 4.5,
        icon: <BellOutlined style={{ color: "#1677ff" }} />,
        onClick: () => {
          setNotificationVisible(true);
          notification.destroy();
        },
      });

      // 重新拉取最新未读数，保证与服务端一致
      fetchUnreadCount();
    };

    sseService.on("notification", handleNotification);
    return () => {
      sseService.off("notification", handleNotification);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // 获取未读通知数量
  const fetchUnreadCount = async () => {
    try {
      const response = await getUnreadNotificationCount();
      if (response.success && response.data !== undefined) {
        setUnreadCount(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  // 处理未读数量变化
  const handleUnreadCountChange = (count) => {
    setUnreadCount(count);
  };

  // 菜单数据独立计算，依赖collapsed和语言变化
  useEffect(() => {
    const staticMenuData = [
      {
        key: "/dashboard",
        name: t("menu.dashboard"),
        icon: "DashboardOutlined",
        path: "/dashboard",
        sort: 0,
      },
      {
        key: "/nationwide-dashboard",
        name: t("menu.nationwideDashboard"),
        icon: "GlobalOutlined",
        path: "/nationwide-dashboard",
        sort: 0.1,
      },
      {
        key: "master-data",
        name: t("menu.masterData"),
        icon: "AppstoreOutlined",
        sort: 1,
        children: [
          {
            key: "/master-data/store-management",
            path: "/master-data/store-management",
            name: t("menu.warehouse"),
            icon: "ShopOutlined",
          },
          {
            key: "/promotion/substitute-rule",
            path: "/promotion/substitute-rule",
            name: t("promotionRule.menu.substituteRule"),
            icon: "SwapOutlined",
          },
          {
            key: "/master-data/item/list",
            path: "/master-data/item/list",
            name: t("menu.itemMgt"),
            icon: "AppstoreOutlined",
          },
          {
            key: "/master-data/category/list",
            path: "/master-data/category/list",
            name: t("menu.categoryMgt"),
            icon: "AppstoreOutlined",
          },
          {
            key: "/master-data/location/list",
            path: "/master-data/location/list",
            name: t("menu.locationMgt"),
            icon: "AppstoreOutlined",
          },
        ],
      },
      {
        key: "order",
        name: t("order.menu.title"),
        icon: "AppstoreOutlined",
        sort: 2,
        children: [
          {
            key: "/order/order/list",
            path: "/order/order/list",
            name: t("order.order.list.title"),
            icon: "AppstoreOutlined",
          },
          {
            key: "/order/return-order/list",
            path: "/order/return-order/list",
            name: t("order.returnOrder.list.title"),
            icon: "AppstoreOutlined",
          },
          {
            key: "/order/shortage-order/list",
            path: "/order/shortage-order/list",
            name: t("order.shortageOrder.list.title"),
            icon: "AppstoreOutlined",
          },
          {
            key: "/order/substitute-management/list",
            path: "/order/substitute-management/list",
            name: t("order.substituteManagement.list.title"),
            icon: "SwapOutlined",
          },
        ],
      },
      {
        key: "picking",
        name: t("picking.menu.title"),
        icon: "AppstoreOutlined",
        sort: 3,
        children: [
          {
            key: "/picking/picking-task/list",
            path: "/picking/picking-task/list",
            name: t("picking.pickingTask.list.title"),
            icon: "AppstoreOutlined",
          },
          {
            key: "/picking/picking-strategy/list",
            path: "/picking/picking-strategy/list",
            name: t("pickingStrategy.menu.title"),
            icon: "AppstoreOutlined",
          },
        ],
      },
      {
        key: "wave",
        name: t("menu.waveMgt"),
        icon: "AppstoreOutlined",
        sort: 5,
        children: [
          {
            key: "/wave/wave-rule",
            path: "/wave/wave-rule",
            name: t("menu.waveStrategy"),
            icon: "AppstoreOutlined",
          },
          {
            key: "/wave/wave-instance",
            path: "/wave/wave-instance",
            name: t("wave.waveInstance.list.title"),
            icon: "AppstoreOutlined",
          },
          {
            key: "/wave/wave-config-log",
            path: "/wave/wave-config-log",
            name: t("menu.waveConfigLog"),
            icon: "HistoryOutlined",
          },
        ],
      },
      {
        key: "inventory",
        name: t("inventory.menu.title"),
        icon: "AppstoreOutlined",
        sort: 6,
        children: [
          {
            key: "/inventory/stock/list",
            path: "/inventory/stock/list",
            name: t("inventory.menu.logicalInventory"),
            icon: "AppstoreOutlined",
          },
        ],
      },
      {
        key: "account",
        name: t("account.title"),
        icon: "TeamOutlined",
        sort: 7,
        children: [
          {
            key: "/account-management",
            path: "/account-management",
            name: t("account.title"),
            icon: "TeamOutlined",
          },
          {
            key: "/role",
            path: "/role",
            name: t("role.title"),
            icon: "SafetyCertificateOutlined",
          },
          {
            key: "/personnel-mgt",
            path: "/personnel-mgt",
            name: t("personnel.title"),
            icon: "TeamOutlined",
          },
        ],
      },
      {
        key: "report",
        name: t("menu.reports"),
        icon: "BarChartOutlined",
        sort: 8,
        children: [
          {
            key: "/report/sla-fulfillment",
            path: "/report/sla-fulfillment",
            name: t("menu.slaFulfillmentReport"),
            icon: "FileTextOutlined",
          },
        ],
      },
    ];
    setMenuItemsData(processMenuItems(staticMenuData, collapsed));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed, i18n.language]); // 使用i18n.language而不t函数

  // 获取仓库列表和当前仓库
  const fetchWarehouseList = async () => {
    try {
      const response = await authService.getUserInfo();
      const userData = response.data;
      
      // 设置当前仓库
      if (userData.userContext.warehouseId && userData.userContext.warehouseId != -1) {
        setCurrentWarehouse({
          id: userData.userContext.warehouseId,
          name: userData.userContext.warehouseName,
          timeZone: userData.userContext.warehouseTimeZone,
        });
        setSelectedWarehouse(userData.userContext.warehouseId);
      }
      
      // 设置仓库列表选项
      if (userData.warehouseList && userData.warehouseList.length > 0) {
        const options = userData.warehouseList.map(warehouse => ({
          value: warehouse.id,
          label: warehouse.name,
          timeZone: warehouse.timeZone,
          code: warehouse.code,
        }));
        setWarehouseList(options);
      }
    } catch (error) {
      message.error(t('switchWarehouse.fetchUserInfoFailed'));
    }
  };

  const handleLogout = () => {
    logout();
    navigateToLogin();
  };

  const handleLanguageChange = (lang, key) => {
    i18n.changeLanguage(lang);
    locale.setLang(key);

    // 重新加载刷新菜单
    window.location.reload();
    // setCookie("x-hng", `lang=${key}`);
  };

  // 处理仓库选择变化
  const handleWarehouseChange = (value) => {
    setSelectedWarehouse(value);
  };

  // 确认切换仓库
  const handleSwitchWarehouse = () => {
    if (!selectedWarehouse || selectedWarehouse === currentWarehouse?.id) {
      return;
    }

    Modal.confirm({
      title: t('switchWarehouse.title'),
      content: t('switchWarehouse.confirm'),
      onOk: async () => {
        try {
          setSwitchLoading(true);
          await authService.updateCurrentWarehouse(selectedWarehouse);
          message.success(t('switchWarehouse.switchSuccess'));
          
          // 刷新页面以更新仓库信息
          setTimeout(() => {
            window.location.reload();
          }, 500);
        } catch (error) {
          message.error(t('switchWarehouse.switchFailed'));
        } finally {
          setSwitchLoading(false);
        }
      },
    });
  };

  // 图标名称映射
  const iconMap = {
    AppstoreOutlined: <AppstoreOutlined />,
    DashboardOutlined: <DashboardOutlined />,
    GlobalOutlined: <GlobalOutlined />,
    BarChartOutlined: <BarChartOutlined />,
    FileTextOutlined: <FileTextOutlined />,
    ShopOutlined: <ShopOutlined />,
    SwapOutlined: <SwapOutlined />,
    TeamOutlined: <TeamOutlined />,
    SafetyCertificateOutlined: <SafetyCertificateOutlined />,
  };

  // 处理菜单项，为每个标签添加 Tooltip
  const processMenuItems = (items, collapsed) => {
    return items.map((item) => {
      const processedItem = {
        ...item,
        title: item?.label ?? item?.name ?? "",
        icon: iconMap[item.icon] || <AppstoreOutlined />,
      };

      // 判断是否为叶子节点（没有children或children为空数组）
      const isLeaf = !item.children || item.children.length === 0;

      // 为标签添加 Tooltip，优先使用 label，其次 name，最后 title
      const displayText = item.label || item.name || item.title;
      if (displayText) {
        processedItem.label = (
          <MenuItemWithTooltip
            label={displayText}
            collapsed={collapsed}
            isLeaf={isLeaf}
          />
        );
      }

      // 递归处理子菜单项
      if (item.children) {
        processedItem.children = processMenuItems(item.children, collapsed);
      }

      return processedItem;
    });
  };

  // 旧的静态菜单配置已移除，现在在 useEffect 中使用动态配置

  // 旧的静态菜单配置已移除，现在使用 useEffect 中的动态配置
  // const menuItems = processMenuItems(originalMenuItems, collapsed);

  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: t("user.profile"),
      onClick: () => navigate("/profile"),
    },
    // {
    //   key: "settings",
    //   icon: <SettingOutlined />,
    //   label: t("user.settings"),
    //   onClick: () => navigate("/settings"),
    // },
    {
      type: "divider",
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("user.logout"),
      onClick: handleLogout,
    },
  ];

  const languageMenuItems = [
    {
      key: "en-US",
      label: "English",
      onClick: () => handleLanguageChange("en", "en-US"),
    },
    {
      key: "th-TH",
      label: "ไทย",
      onClick: () => handleLanguageChange("th", "th-TH"),
    },
    // {
    //   key: "zh-CN",
    //   label: "中文",
    //   onClick: () => handleLanguageChange("zh", "zh-CN"),
    // },
  ];

  // 通知菜单项（已移除，改为弹窗）

  return (
    <Layout className="main-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme="dark"
        className="adaptive-sider"
      >
        <div style={{ padding: "16px", textAlign: "center", color: "white" }}>
          <h3 style={{ color: "white", margin: 0 }}>{t("login.title")}</h3>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={menuItemsData.map((item) => item.key)} // 默认展开所有一级菜单
          items={menuItemsData}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            padding: "0 16px",
            background: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 1px 4px rgba(0,21,41,.08)",
            height: 40,
            lineHeight: "40px",
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: "16px", width: 40, height: 40 }}
          />

          <Space size="middle">
            {/* 仓库切换 Select */}
            {warehouseList.length > 0 && (
              <Space.Compact>
                <Select
                  value={selectedWarehouse}
                  onChange={handleWarehouseChange}
                  style={{ minWidth: 150 }}
                  showSearch
                  placeholder={t('switchWarehouse.targetWarehousePlaceholder')}
                  optionFilterProp="label"
                  options={warehouseList}
                  suffixIcon={<ShopOutlined />}
                />
                <Button 
                  type="primary" 
                  onClick={handleSwitchWarehouse}
                  loading={switchLoading}
                  disabled={!selectedWarehouse || selectedWarehouse === currentWarehouse?.id}
                >
                  {t('switchWarehouse.confirm')}
                </Button>
              </Space.Compact>
            )}

            {/* 通知入口 - 放在 Language 左边，点击打开通知中心弹窗 */}
            <Badge count={unreadCount} size="small" offset={[-2, 2]}>
              <Button
                type="text"
                icon={<BellOutlined />}
                onClick={() => setNotificationVisible(true)}
              />
            </Badge>

            <Dropdown
              menu={{ items: languageMenuItems }}
              placement="bottomRight"
            >
              <Button type="text" icon={<GlobalOutlined />}>
                {t("common.language")}
              </Button>
            </Dropdown>

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div className="user-avatar">
                <Avatar
                  src={user?.avatar}
                  icon={<UserOutlined />}
                  size="small"
                />
                {!collapsed && (
                  <span className="user-info" style={{ marginLeft: 8 }}>
                    {user?.name === '管理员' ? 'Admin' : user?.name}
                  </span>
                )}
              </div>
            </Dropdown>
          </Space>
        </Header>
        <Content className="site-layout-content">
          <KeepAliveTab
            titleKeyMap={titleKeyMap}
            outlet={useOutlet()}
            location={location}
            onChange={(path) => navigate(path)}
          />
        </Content>
      </Layout>
      {/* 通知中心弹窗 */}
      <NotificationCenter
        visible={notificationVisible}
        onClose={() => setNotificationVisible(false)}
        onUnreadCountChange={handleUnreadCountChange}
      />
    </Layout>
  );
};

export default MainLayout;
